/**
 * Pure canvas geometry (Tech-decision "Coordinate model"): abstract grid units, snap-to-grid applied
 * at pointer-up (drags stay fluid, persisted data stays tidy), and rotation normalization. No DOM,
 * no React — every function here is table-testable in isolation, which is the whole point of the
 * plain-SVG + pointer-events choice over an opaque canvas library.
 */

/** The abstract grid the plan snaps to. Positions persist in these units, not screen pixels. */
export const GRID_UNIT = 10;

/** The default room extent (grid units) — the SVG `viewBox` a zone renders into. */
export const ROOM_WIDTH = 800;
export const ROOM_HEIGHT = 600;

/** Nudge step for arrow-key movement (Shift = coarse). Keyboard parity for every drag (a11y path). */
export const NUDGE_FINE = GRID_UNIT;
export const NUDGE_COARSE = GRID_UNIT * 5;

export const ROTATE_STEP = 15;

export function snap(value: number, grid: number = GRID_UNIT): number {
  return Math.round(value / grid) * grid;
}

export function snapPoint(
  x: number,
  y: number,
  grid: number = GRID_UNIT,
): { x: number; y: number } {
  return { x: snap(x, grid), y: snap(y, grid) };
}

/** Clamp a point inside the room bounds so a table can never be dragged off-canvas. */
export function clampToRoom(
  x: number,
  y: number,
  width = ROOM_WIDTH,
  height = ROOM_HEIGHT,
): { x: number; y: number } {
  return {
    x: Math.min(Math.max(x, 0), width),
    y: Math.min(Math.max(y, 0), height),
  };
}

/** Normalize a rotation to the `[0, 360)` range (§C.5 stores `rotationDeg` as a plain Int). */
export function normalizeRotation(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

export function rotateBy(current: number, delta: number): number {
  return normalizeRotation(current + delta);
}

/**
 * Convert a screen-space pointer delta (CSS px) to grid units for a zone rendered at `scale`
 * (viewBox units per CSS px). Pan/zoom is client-only state; only the resulting grid position
 * persists, so this is where device pixels are discarded.
 */
export function screenDeltaToGrid(
  dxPx: number,
  dyPx: number,
  scale = 1,
): { dx: number; dy: number } {
  return { dx: dxPx / scale, dy: dyPx / scale };
}

/** The default footprint (grid units) of a freshly placed table, by shape. */
export function defaultSize(shape: string): { w: number; h: number } {
  switch (shape) {
    case "RECTANGLE":
    case "OVAL":
      return { w: 90, h: 50 };
    case "ROUND":
      return { w: 60, h: 60 };
    case "CUSTOM":
      return { w: 70, h: 70 };
    default:
      return { w: 60, h: 60 };
  }
}
